import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { driver, type Driver, type DriverHook, type DriveStep, type PopoverDOM } from 'driver.js';
import { useLanguage } from '@contexts/LanguageContext';
import { useTheme } from '@contexts/ThemeContext';
import { LANGUAGES, type LanguageCode } from '@constants/translations';
import { DARK_THEME_MODES } from '@constants/colors';
import { impersonationUtils } from '@utils/impersonation';
import { useOptionalPatchLogGate } from '@/features/patch-logs/gate-context';
import { useOptionalOnboardingGate } from '@/features/first-login/gate-context';
import { resolveTourCopy } from './tour-copy';
import { tourById, tourForPathname } from './tour-registry';
import { getTourUserId, hasSeenTour, markTourSeen, resetSeenTours } from './tour-storage';
import type { TourDefinition, TourGate, TourStep } from './types';
import 'driver.js/dist/driver.css';
import './onboarding.css';

/** Lets the page's mount animation settle before the spotlight measures its target. */
const AUTO_START_DELAY_MS = 450;

/** Quiet time after the last value change before an interactive step counts as done. */
const INTERACTION_IDLE_MS = 900;

interface TourContextValue {
  /** Tour for the current route, if any — drives the "?" pill. */
  pageTour: TourDefinition | undefined;
  /** Tour for an in-page surface (the open PIB tab), registered by `useSubTour`. */
  subTour: TourDefinition | undefined;
  /** Human label for the sub-tour's surface, e.g. the tab name. */
  subTourLabel: string | undefined;
  startTour: (tour: TourDefinition, startIndex?: number) => void;
  /** Start the tour partway in, at the step with this key. */
  startTourAt: (tour: TourDefinition, stepKey: string) => void;
  startTourById: (tourId: string) => void;
  /** Named parts of the current page's tour whose opening step is on screen. */
  sections: Array<{ key: string; stepKey: string }>;
  registerSubTour: (tour: TourDefinition | undefined, label?: string) => void;
  /** True while a tour is on screen — sub-tours must never interrupt one. */
  isRunning: boolean;
  /** Same answer, read synchronously — state is a render behind. */
  isTourRunning: () => boolean;
}

const TourContext = createContext<TourContextValue | null>(null);

/** Null outside the provider — isolated components and tests tolerate that. */
export const useOptionalTour = () => useContext(TourContext);

/**
 * Onboarding coach marks. Auto-runs a surface's tour once per user, and exposes
 * the replay handles the "?" pill needs. Copy resolves at start time, so the
 * in-popover language switcher can restart the tour in the new language at the
 * same step.
 */
export const TourProvider = ({ children }: { children: ReactNode }) => {
  const { pathname, search } = useLocation();
  const { language, setLanguage } = useLanguage();
  const { mode, colors: themeColors } = useTheme();
  const driverRef = useRef<Driver | null>(null);
  const [subTour, setSubTour] = useState<TourDefinition | undefined>(undefined);
  const [subTourLabel, setSubTourLabel] = useState<string | undefined>(undefined);
  const [isRunning, setIsRunning] = useState(false);
  // React state lands a render late; a sub-tour deciding whether to start needs
  // the answer at the moment its timer fires, not one render ago.
  const runningRef = useRef(false);
  // The "What's New" modal owns the screen right after login; the tour must
  // not spotlight anything underneath it. Null when mounted without the gate.
  const isPatchLogBlocking = useOptionalPatchLogGate()?.isBlocking ?? false;
  // Same rule for the first-login wizard: it owns the screen until the user
  // finishes or defers it, and a page tour must not start underneath it.
  const onboardingGate = useOptionalOnboardingGate();
  const isOnboardingBlocking = onboardingGate?.isBlocking ?? false;
  // Once the wizard has been deferred or finished in this mount, no page tour
  // auto-starts either; the next login gets them as usual.
  const pageToursSuppressed = onboardingGate?.suppressPageTours ?? false;

  const pageTour = useMemo(() => tourForPathname(pathname, search), [pathname, search]);

  // The popover and pill live outside React's tree (driver.js appends to body),
  // so the theme reaches them through custom properties on the root element.
  // Re-runs on every theme change, which is what keeps all 15 palettes correct.
  useEffect(() => {
    const root = document.documentElement;
    const isDark = DARK_THEME_MODES.includes(mode);
    const vars: Record<string, string> = {
      '--jarvis-tour-bg': themeColors.card,
      '--jarvis-tour-text': themeColors.text,
      '--jarvis-tour-muted': themeColors.textSecondary,
      '--jarvis-tour-border': themeColors.border,
      '--jarvis-tour-primary': themeColors.primary,
      '--jarvis-tour-on-primary': themeColors.buttonText ?? '#FFFFFF',
      '--jarvis-tour-surface': themeColors.formFields,
      // Dark palettes need a heavier scrim or the spotlight cutout stops reading.
      '--jarvis-tour-scrim': isDark ? 'rgba(0, 0, 0, 0.72)' : 'rgba(15, 23, 42, 0.55)',
    };
    for (const [name, value] of Object.entries(vars)) {
      root.style.setProperty(name, value);
    }
  }, [mode, themeColors]);

  // Language is read through a ref so `startTour` stays referentially stable:
  // it must not be re-created on every language change, or the auto-start
  // effect below would re-fire and restart a tour the user is already in.
  const languageRef = useRef<LanguageCode>(language);
  languageRef.current = language;

  const startTour = useCallback(
    (tour: TourDefinition, startIndex = 0) => {
      const run = (index: number, lang: LanguageCode) => {
        stopStepSync?.();
        driverRef.current?.destroy();
        // A popover that outlived its instance would sit on screen forever with
        // no way to dismiss it.
        for (const stale of document.querySelectorAll('.driver-popover')) {
          stale.remove();
        }
        // Resolved per start, not per definition: what is on screen decides
        // which steps the tour actually contains.
        const steps = runnableSteps(tour);
        if (steps.length === 0) {
          return;
        }
        const t = (key: string, vars?: Record<string, string | number>) => resolveTourCopy(lang, key, vars);
        const instance = driver({
          steps: buildDriveSteps(steps, tour, t),
          showProgress: true,
          progressText: t('controls.progress', { current: '{{current}}', total: '{{total}}' }),
          nextBtnText: t('controls.next'),
          prevBtnText: t('controls.back'),
          doneBtnText: t('controls.done'),
          popoverClass: 'jarvis-tour-popover',
          stagePadding: 8,
          stageRadius: 12,
          smoothScroll: !prefersReducedMotion(),
          animate: !prefersReducedMotion(),
          overlayColor: readCssVar('--jarvis-tour-scrim') || 'rgba(15, 23, 42, 0.55)',
          overlayOpacity: 1,
          // Steps opt back in individually; everything else stays inert so a
          // stray click can't derail the tour.
          disableActiveInteraction: true,
          // Clicking the dim used to close the tour outright, which is how
          // people lost their place while reaching for something on the page.
          // Leaving is still one Esc or one ✕ away.
          overlayClickBehavior: () => {},
          onPopoverRender: (popover, opts) => {
            appendLanguageSwitcher(popover, lang, (code) => {
              const activeIndex = opts.driver.getActiveIndex() ?? opts.index ?? index;
              // Change the app language first, then rebuild the tour at the same
              // step. `run` is passed the new code explicitly — reading it back
              // from context would still yield the old value at this point.
              setLanguage(code);
              run(activeIndex, code);
            });
          },
          onDestroyed: () => {
            stopStepSync?.();
            stopGateWatcher?.();
            stopActiveWatcher?.();
            removeDismissListener?.();
            document.documentElement.classList.remove('jarvis-tour-running');
            document.documentElement.removeAttribute('data-tour-anchor');
            if (driverRef.current === instance) {
              driverRef.current = null;
              runningRef.current = false;
              setIsRunning(false);
            }
          },
        });
        driverRef.current = instance;
        document.documentElement.classList.add('jarvis-tour-running');
        runningRef.current = true;
        setIsRunning(true);
        instance.drive(index);

        /*
         * Keep the step list in step with the page.
         *
         * Some steps describe controls that appear part-way through the tour —
         * the split panel only exists once the user opens a file in the preview,
         * which the preview step asks them to do. Resolving the list once at
         * start would leave those steps permanently dropped, and driver.js
         * renders "Done" as soon as nothing reachable lies ahead (it scans
         * forward for the next present element), so the tour would simply end
         * there.
         *
         * Recompute whenever the DOM settles, and hand driver.js a new list only
         * when the set has genuinely changed — re-driving on every mutation
         * would restart the current step constantly.
         */
        const syncSteps = () => {
          if (driverRef.current !== instance || !instance.isActive()) {
            return;
          }
          const next = runnableSteps(tour);
          if (next.length === steps.length && next.every((step, i) => step.key === steps[i].key)) {
            return;
          }
          const activeKey = steps[instance.getActiveIndex() ?? 0]?.key;
          const nextIndex = Math.max(
            0,
            next.findIndex((step) => step.key === activeKey)
          );
          // Rebuild from scratch rather than setSteps + drive on the live
          // instance: driver.js renders a SECOND popover when driven while
          // already active, which showed up as two stacked popovers with
          // different step counts. run() destroys first, so there is only ever
          // one.
          run(nextIndex, lang);
        };

        let settle = 0;
        const observer = new MutationObserver(() => {
          window.clearTimeout(settle);
          settle = window.setTimeout(syncSteps, 300);
        });
        observer.observe(document.body, { childList: true, subtree: true });
        stopStepSync?.();
        stopStepSync = () => {
          observer.disconnect();
          window.clearTimeout(settle);
          stopStepSync = null;
        };
      };
      run(startIndex, languageRef.current);
    },
    [setLanguage]
  );

  // Index is into the runnable list, which is what the tour actually contains —
  // the definition's index would be wrong whenever a step has been dropped.
  const startTourAt = useCallback(
    (tour: TourDefinition, stepKey: string) => {
      const index = runnableSteps(tour).findIndex((step) => step.key === stepKey);
      startTour(tour, index < 0 ? 0 : index);
    },
    [startTour]
  );

  const startTourById = useCallback(
    (tourId: string) => {
      const tour = tourById(tourId);
      if (tour) {
        startTour(tour);
      }
    },
    [startTour]
  );

  // View-once auto-start for the route's tour. Marked seen at START, so
  // dismissing still counts as viewed and the user is never re-ambushed.
  // Re-runs once the patch-log modal releases the screen.
  useEffect(() => {
    if (
      !pageTour ||
      impersonationUtils.isImpersonating() ||
      isPatchLogBlocking ||
      isOnboardingBlocking ||
      pageToursSuppressed
    ) {
      return;
    }
    const userId = getTourUserId();
    if (!userId || hasSeenTour(userId, pageTour.id)) {
      return;
    }
    const timer = window.setTimeout(() => {
      markTourSeen(userId, pageTour.id);
      startTour(pageTour);
    }, AUTO_START_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [pageTour, startTour, isPatchLogBlocking, isOnboardingBlocking, pageToursSuppressed]);

  // Leaving the route tears down any running tour — a terminal step may have
  // navigated, and the destination's own tour takes over.
  useEffect(() => {
    return () => {
      driverRef.current?.destroy();
    };
  }, [pathname]);

  // Escape must close the TOUR, not whatever is behind it. Mantine modals and
  // Radix-style overlays listen on document; taking Escape at window capture
  // and tearing the tour down ourselves means only one thing reacts.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !driverRef.current) {
        return;
      }
      event.stopPropagation();
      event.preventDefault();
      driverRef.current.destroy();
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, []);

  // QA affordance: re-arm every tour in this browser without clearing the session.
  useEffect(() => {
    (window as unknown as { __resetTours?: () => void }).__resetTours = () => {
      resetSeenTours();
      console.info('[onboarding] seen flags cleared — reload to see tours again');
    };
    return () => {
      (window as unknown as { __resetTours?: () => void }).__resetTours = undefined;
    };
  }, []);

  const registerSubTour = useCallback((tour: TourDefinition | undefined, label?: string) => {
    setSubTour(tour);
    setSubTourLabel(label);
  }, []);

  // Recomputed on every render rather than memoised: whether a section's anchor
  // is on screen changes with page state, not with the route.
  const sections = pageTour
    ? runnableSteps(pageTour)
        .filter((step) => step.section)
        .map((step) => ({ key: step.section as string, stepKey: step.key }))
    : [];

  const value = useMemo<TourContextValue>(
    () => ({
      pageTour,
      subTour,
      subTourLabel,
      sections,
      startTour,
      startTourAt,
      startTourById,
      registerSubTour,
      isRunning,
      isTourRunning: () => runningRef.current,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      pageTour,
      subTour,
      subTourLabel,
      JSON.stringify(sections),
      startTour,
      startTourAt,
      startTourById,
      registerSubTour,
      isRunning,
    ]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

/* ------------------------------------------------------------------ *
 *  Step interaction modes
 * ------------------------------------------------------------------ */

/** Teardown for the highlighted step's gate watcher. */
let stopGateWatcher: (() => void) | null = null;

/**
 * Holds the tour at a step until the page satisfies its condition.
 *
 * Disables Next and shows what is still missing, re-checking on a short poll —
 * the conditions are things like "a file has been staged" or "no segment is
 * still empty", which change through paths we do not control and would not
 * catch from events alone.
 *
 * Deliberately does NOT block ✕ or Esc: the tour may guide, but it must never
 * trap someone in it.
 */
const makeGateWatcher =
  (gate: TourGate, hint: string, autoAdvance: boolean): DriverHook =>
  (_element, _step, opts) => {
    stopGateWatcher?.();
    const popover = opts.state.popover;
    if (!popover) {
      return;
    }
    const button = popover.nextButton;
    const notice = document.createElement('div');
    notice.className = 'jarvis-tour-gate';
    notice.textContent = hint;
    popover.footer.insertAdjacentElement('beforebegin', notice);

    const check = () => {
      const matches = document.querySelectorAll(gate.selector).length;
      return gate.absent ? matches === 0 : matches >= (gate.min ?? 1);
    };
    // Only a transition counts. A step whose condition already holds should
    // still be readable rather than flashing past.
    let satisfiedBefore = check();

    const evaluate = () => {
      const satisfied = check();
      button.disabled = !satisfied;
      button.classList.toggle('driver-popover-btn-disabled', !satisfied);
      notice.style.display = satisfied ? 'none' : '';
      if (autoAdvance && satisfied && !satisfiedBefore) {
        stopGateWatcher?.();
        opts.driver.moveNext();
        return;
      }
      satisfiedBefore = satisfied;
    };
    evaluate();
    const interval = window.setInterval(evaluate, 300);
    stopGateWatcher = () => {
      window.clearInterval(interval);
      notice.remove();
      button.disabled = false;
      button.classList.remove('driver-popover-btn-disabled');
      stopGateWatcher = null;
    };
  };

const clearGateWatcher: DriverHook = () => {
  stopGateWatcher?.();
};

/** Teardown for the running tour's step-list watcher. */
let stopStepSync: (() => void) | null = null;

/** At most one interactive step is watched at a time. */
let stopActiveWatcher: (() => void) | null = null;

/**
 * Watches the highlighted area's form controls; once any value changes from its
 * initial state and input goes quiet, the step counts as done and the tour
 * advances. Polling rather than listeners, because Mantine's custom pickers set
 * values without firing native events, and controls can mount after the step.
 */
const startChangeWatcher: DriverHook = (element, _step, opts) => {
  stopActiveWatcher?.();
  if (!element) {
    return;
  }
  const values = () =>
    Array.from(element.querySelectorAll('input, select, textarea'))
      .map((control) => (control as HTMLInputElement).value ?? '')
      .join('');
  const initial = values();
  let last = initial;
  let idleTimer: number | undefined;
  const interval = window.setInterval(() => {
    const current = values();
    if (current === last) {
      return;
    }
    last = current;
    window.clearTimeout(idleTimer);
    if (current !== initial) {
      idleTimer = window.setTimeout(() => {
        stopActiveWatcher?.();
        opts.driver.moveNext();
      }, INTERACTION_IDLE_MS);
    }
  }, 250);
  stopActiveWatcher = () => {
    window.clearInterval(interval);
    window.clearTimeout(idleTimer);
    stopActiveWatcher = null;
  };
};

const stopChangeWatcher: DriverHook = () => {
  stopActiveWatcher?.();
};

/** `dismissOnClick` teardown for the currently highlighted element. */
let removeDismissListener: (() => void) | null = null;

/**
 * Capture phase, so the tour is gone before the element's own handler opens a
 * modal — nothing ends up trapped underneath the tour dim.
 */
const startDismissOnClick: DriverHook = (element, _step, opts) => {
  removeDismissListener?.();
  if (!element) {
    return;
  }
  const onClick = () => {
    removeDismissListener?.();
    opts.driver.destroy();
  };
  element.addEventListener('click', onClick, { capture: true, once: true });
  removeDismissListener = () => {
    element.removeEventListener('click', onClick, { capture: true });
    removeDismissListener = null;
  };
};

const stopDismissOnClick: DriverHook = () => {
  removeDismissListener?.();
};

/** driver.js exposes one hook per event, so anything extra has to be chained. */
const composeHooks =
  (...hooks: Array<DriverHook | undefined>): DriverHook =>
  (element, step, opts) => {
    for (const hook of hooks) {
      hook?.(element, step, opts);
    }
  };

/**
 * Mirrors the active step's anchor onto <html data-tour-anchor>. The "?" pill
 * hides itself for the duration of a tour, which would leave the step that
 * points AT the pill measuring a display:none box — the popover then lands in
 * the top-left corner instead of beside it. CSS uses this attribute to keep the
 * pill visible for exactly that step.
 */
const markActiveAnchor =
  (anchor: string | undefined): DriverHook =>
  () => {
    if (anchor) {
      document.documentElement.setAttribute('data-tour-anchor', anchor);
    } else {
      document.documentElement.removeAttribute('data-tour-anchor');
    }
  };

type CopyResolver = (key: string, vars?: Record<string, string | number>) => string;

/**
 * Steps that can actually run right now: anchor is on the page, or the step
 * declares that its anchor is still coming.
 *
 * Presence in the DOM is the rule for EVERY step, deliberately. An earlier
 * version asked each step to declare itself conditional, and that failed twice
 * in a row because it depends on guessing which anchors are conditional — the
 * upload screen's whole footer, Reset and Process Dokumen included, is behind
 * `uploadedFiles.length > 0`, which was not obvious from the tour definition.
 *
 * Leaving an unreachable step in is not a harmless no-op. driver.js stalls for
 * `waitForElement` and then jumps, which reads as the tour breaking; the
 * progress count promises steps that never appear; and once nothing ahead is
 * reachable it labels the current step as the last and renders "Done", leaving
 * the user mid-tour with no way to continue.
 *
 * `waitForElement` is the opt-out, for anchors that really are on their way
 * (lazy routes, Suspense) rather than absent by page state.
 */
const runnableSteps = (tour: TourDefinition) =>
  tour.steps.filter(
    (step) =>
      !step.anchor ||
      step.waitForElement !== undefined ||
      document.querySelector(`[data-tour="${step.anchor}"]`) !== null
  );

const buildDriveSteps = (steps: TourStep[], tour: TourDefinition, t: CopyResolver): DriveStep[] =>
  steps.map((step) => {
    const body = t(`tours.${tour.id}.${step.key}.body`);
    const hint =
      step.allowClick || step.dismissOnClick
        ? t('controls.clickToContinue')
        : step.advanceOnChange
          ? t('controls.interactHint')
          : step.interactive
            ? t('controls.exploreHint')
            : undefined;
    const interactive = Boolean(step.advanceOnChange || step.allowClick || step.dismissOnClick || step.interactive);
    return {
      element: step.anchor ? `[data-tour="${step.anchor}"]` : undefined,
      // A missing anchor skips its step rather than stranding the user on a
      // spotlight over nothing — conditional UI (banners, panels) comes and goes.
      skipMissingElement: true,
      ...(step.waitForElement ? { waitForElement: step.waitForElement } : {}),
      ...(interactive ? { disableActiveInteraction: false } : {}),
      // The real click performs the action AND advances the tour.
      ...(step.allowClick ? { advanceOnClick: true } : {}),
      // Every step publishes its anchor; extra behaviour composes on top rather
      // than overwriting, since driver.js allows only one hook per event.
      onHighlightStarted: composeHooks(
        markActiveAnchor(step.anchor),
        step.advanceOnChange ? startChangeWatcher : undefined,
        step.dismissOnClick ? startDismissOnClick : undefined
      ),
      ...(step.gate
        ? {
            onHighlighted: makeGateWatcher(
              step.gate,
              t(`tours.${tour.id}.${step.key}.gate`),
              Boolean(step.autoAdvance)
            ),
          }
        : {}),
      ...(step.advanceOnChange || step.dismissOnClick || step.gate
        ? {
            onDeselected: composeHooks(
              step.advanceOnChange ? stopChangeWatcher : undefined,
              step.dismissOnClick ? stopDismissOnClick : undefined,
              step.gate ? clearGateWatcher : undefined
            ),
          }
        : {}),
      popover: {
        title: t(`tours.${tour.id}.${step.key}.title`),
        description: hint ? `${body}<br /><span class="jarvis-tour-hint">${hint}</span>` : body,
        side: step.side,
        align: step.align,
      },
    };
  });

/**
 * Compact language row inside the popover, so the user can switch language
 * mid-tour without losing their place. Built from `LANGUAGES`, so registering
 * more codes app-wide extends this automatically.
 */
const appendLanguageSwitcher = (popover: PopoverDOM, active: LanguageCode, onSelect: (code: LanguageCode) => void) => {
  if (LANGUAGES.length < 2) {
    return;
  }
  const row = document.createElement('div');
  row.className = 'jarvis-tour-lang';
  for (const meta of LANGUAGES) {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = meta.code === 'id' ? 'ID' : meta.label;
    button.setAttribute('aria-label', meta.label);
    if (meta.code === active) {
      button.classList.add('jarvis-tour-lang-active');
      button.disabled = true;
    } else {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        onSelect(meta.code);
      });
    }
    row.appendChild(button);
  }
  popover.description.insertAdjacentElement('afterend', row);
};

const readCssVar = (name: string) => {
  if (typeof window === 'undefined') {
    return '';
  }
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
